import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Brain, Loader2 } from "lucide-react";
import { api } from "../lib/api";

// iter-213 — Global AI job bubble. Polls for the current user's
// most-recent in-flight estimate/analyze every 10s and renders a
// draggable floating bubble (identical shape to the one inside
// AnalyzingOverlay) on ANY page of the app — not just the project
// workspace. Tapping it navigates the user to the originating
// project.
//
// iter-215 — Wrapped in an error boundary so a bug here can NEVER
// render the whole app useless. Any thrown error silently hides the
// bubble; the rest of the app keeps working.
const POS_KEY = "pf.aiBubble.pos";
const POLL_MS = 10_000;

class BubbleErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { crashed: false };
    }
    static getDerivedStateFromError() {
        return { crashed: true };
    }
    componentDidCatch(err) {
        // eslint-disable-next-line no-console
        console.warn("GlobalAiBubble crashed, hiding:", err);
    }
    render() {
        if (this.state.crashed) return null;
        return this.props.children;
    }
}

export default function GlobalAiBubbleWrapper() {
    return (
        <BubbleErrorBoundary>
            <GlobalAiBubbleInner />
        </BubbleErrorBoundary>
    );
}

function readPos() {
    try {
        const p = JSON.parse(localStorage.getItem(POS_KEY) || "null");
        if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) return p;
    } catch { /* noop */ }
    const w = typeof window !== "undefined" ? window.innerWidth : 1024;
    const h = typeof window !== "undefined" ? window.innerHeight : 768;
    return { x: Math.max(16, w - 96 - 24), y: Math.max(16, h - 96 - 24) };
}

function GlobalAiBubbleInner() {
    const [job, setJob] = useState(null);
    const [pos, setPos] = useState(readPos);
    const dragStart = useRef(null);
    const didDrag = useRef(false);
    const timerRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    const inProjectPath = /^\/workspace\/([a-zA-Z0-9-]+)/.exec(location.pathname);
    const currentProjectId = inProjectPath?.[1] || null;

    // On every path change AND periodically — poll for the active job.
    const poll = useCallback(async () => {
        try {
            if (!localStorage.getItem("planflow.auth_token")) {
                setJob(null);
                return;
            }
            const { data } = await api.get("/ai-jobs/active");
            setJob(data?.job || null);
        } catch {
            // 401 / 404 / network — silently skip
            setJob(null);
        }
    }, []);

    useEffect(() => {
        // Clear any prior interval so we never leak between path
        // changes (which re-run this effect).
        if (timerRef.current) clearInterval(timerRef.current);
        if (!localStorage.getItem("planflow.auth_token")) {
            setJob(null);
            return () => {};
        }
        poll();
        timerRef.current = setInterval(poll, POLL_MS);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            timerRef.current = null;
        };
    }, [poll, location.pathname]);

    useEffect(() => {
        try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch { /* noop */ }
    }, [pos]);

    useEffect(() => {
        const clamp = () => setPos((p) => ({
            x: Math.min(Math.max(0, p.x), Math.max(0, window.innerWidth - 96)),
            y: Math.min(Math.max(0, p.y), Math.max(0, window.innerHeight - 96)),
        }));
        window.addEventListener("resize", clamp);
        return () => window.removeEventListener("resize", clamp);
    }, []);

    const onPointerDown = (e) => {
        e.target.setPointerCapture?.(e.pointerId);
        dragStart.current = { x: e.clientX, y: e.clientY, bx: pos.x, by: pos.y };
        didDrag.current = false;
    };
    const onPointerMove = (e) => {
        if (!dragStart.current) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        if (!didDrag.current && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
            didDrag.current = true;
        }
        if (didDrag.current) {
            setPos({
                x: Math.min(Math.max(0, dragStart.current.bx + dx), window.innerWidth - 96),
                y: Math.min(Math.max(0, dragStart.current.by + dy), window.innerHeight - 96),
            });
        }
    };
    const onPointerUp = (e) => {
        e.target.releasePointerCapture?.(e.pointerId);
        const wasDrag = didDrag.current;
        dragStart.current = null;
        didDrag.current = false;
        if (!wasDrag && job?.project_id) {
            // Navigate to the originating project. The WorkspacePage's
            // resumeActiveEstimate effect will re-attach to this job
            // and open the overlay automatically.
            navigate(`/workspace/${job.project_id}`);
        }
    };

    // Hide while on the originating project (WorkspacePage shows its own)
    // and when there's no running job.
    if (!job) return null;
    if (currentProjectId && currentProjectId === job.project_id) return null;

    const pct = typeof job.progress_pct === "number"
        ? Math.max(0, Math.min(100, job.progress_pct))
        : 0;
    const radius = 42;
    const circ = 2 * Math.PI * radius;
    const dash = (circ * pct) / 100;
    const kind = job.kind === "estimate" ? "Estimate"
        : job.kind === "analyze" ? "Analysis"
            : job.kind === "takeoff" ? "Takeoff" : "AI";

    return (
        <div
            data-testid="global-ai-bubble"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            title={`${kind} in progress — tap to open project · ${(job.stage || "").slice(0, 80)}`}
            style={{
                left: pos.x,
                top: pos.y,
                touchAction: "none",
            }}
            className="fixed z-[9999] w-24 h-24 rounded-full bg-[#0A1128] border-2 border-[#00A3FF] shadow-[0_8px_32px_rgba(0,163,255,0.4),0_0_0_1px_rgba(255,255,255,0.08)] cursor-grab active:cursor-grabbing select-none flex items-center justify-center"
        >
            <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
                <circle
                    cx="50" cy="50" r={radius}
                    fill="none"
                    stroke="#00A3FF"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={`${dash} ${circ - dash}`}
                    style={{ transition: "stroke-dasharray 320ms ease-out" }}
                />
            </svg>
            <div className="flex flex-col items-center justify-center pointer-events-none">
                {pct > 0 ? (
                    <Brain className="w-5 h-5 text-[#00A3FF] drop-shadow-[0_0_6px_rgba(0,163,255,0.6)]" />
                ) : (
                    <Loader2 className="w-5 h-5 text-[#00A3FF] animate-spin" />
                )}
                <span className="text-[10px] font-bold text-white tabular-nums leading-tight mt-0.5">
                    {pct > 0 ? `${Math.round(pct)}%` : kind}
                </span>
            </div>
            <span className="absolute inset-0 rounded-full border-2 border-[#00A3FF]/40 animate-ping opacity-40 pointer-events-none" />
        </div>
    );
}
